import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { adminAPI, aiAPI } from '../../lib/api';
import { toast } from 'sonner';
import { 
  Sparkles, MessageCircle, Users, TrendingUp, Plus, Trash2,
  AlertTriangle, Zap, Save, RefreshCw, BarChart3, Settings,
  UserPlus, CreditCard, ThumbsUp, ThumbsDown
} from 'lucide-react';
import { cn } from '../../lib/utils';

export default function AISettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [purchasingPackage, setPurchasingPackage] = useState(null);
  const [config, setConfig] = useState({
    enabled: true,
    welcome_message: 'Hi! How can I help you today?',
    personality: 'friendly',
    custom_instructions: '',
    qa_pairs: []
  });
  const [analytics, setAnalytics] = useState(null);
  const [credits, setCredits] = useState(null);
  const [creditPackages, setCreditPackages] = useState([]);
  const [feedbackData, setFeedbackData] = useState(null);
  const [newQA, setNewQA] = useState({ question: '', answer: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [configRes, analyticsRes, creditsRes, packagesRes, feedbackRes] = await Promise.all([
        adminAPI.getAIConfig(),
        adminAPI.getAIAnalytics(),
        aiAPI.getCredits(),
        aiAPI.getCreditPackages(),
        adminAPI.getAIFeedback().catch(() => ({ data: null }))
      ]);
      
      setConfig(configRes.data || {
        enabled: true,
        welcome_message: 'Hi! How can I help you today?',
        personality: 'friendly',
        custom_instructions: '',
        qa_pairs: []
      });
      setAnalytics(analyticsRes.data || {});
      setCredits(creditsRes.data || {});
      setCreditPackages(packagesRes.data?.packages || []);
      setFeedbackData(feedbackRes.data || null);
    } catch (error) {
      console.error('Failed to load AI data:', error);
      toast.error('Failed to load AI settings');
    } finally {
      setLoading(false);
    }
  };

  const purchaseCredits = async (packageId) => {
    setPurchasingPackage(packageId);
    try {
      const res = await aiAPI.purchaseCredits(packageId);
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      console.error('Failed to purchase credits:', error);
      toast.error('Failed to initiate purchase');
    } finally {
      setPurchasingPackage(null);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await adminAPI.updateAIConfig(config);
      toast.success('AI settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addQAPair = async () => {
    if (!newQA.question || !newQA.answer) {
      toast.error('Please fill in both question and answer');
      return;
    }
    
    try {
      await adminAPI.addQAPair(newQA);
      setConfig(prev => ({
        ...prev,
        qa_pairs: [...(prev.qa_pairs || []), newQA]
      }));
      setNewQA({ question: '', answer: '' });
      toast.success('Q&A pair added');
    } catch (error) {
      toast.error('Failed to add Q&A pair');
    }
  };

  const deleteQAPair = async (index) => {
    try {
      await adminAPI.deleteQAPair(index);
      setConfig(prev => ({
        ...prev,
        qa_pairs: prev.qa_pairs.filter((_, i) => i !== index)
      }));
      toast.success('Q&A pair deleted');
    } catch (error) {
      toast.error('Failed to delete Q&A pair');
    }
  };

  // Credit warning thresholds
  const getCreditWarning = () => {
    if (!credits) return null;
    const total = credits.total_available || 0;
    const initialCredits = (credits.balance || 0) + (credits.free_remaining || 0) + (credits.total_available || 0);
    
    if (total <= 5) {
      return { level: 'critical', message: 'Critical: Less than 5 credits remaining!', color: 'red' };
    } else if (total <= 20) {
      return { level: 'warning', message: 'Warning: Running low on AI credits', color: 'amber' };
    } else if (total <= 50) {
      return { level: 'info', message: 'Consider topping up your AI credits soon', color: 'blue' };
    }
    return null;
  };

  const creditWarning = getCreditWarning();

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-black rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-[#0A0A0A] tracking-tight">AI Assistant Settings</h1>
            <p className="text-sm text-[#525252] mt-1">Configure your AI chatbot for lead generation</p>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={loadData}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#525252] bg-white border border-[#E5E5E5] rounded-lg hover:bg-[#F5F5F5] transition-all"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={saveConfig}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-black rounded-lg hover:bg-[#262626] transition-all disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        {/* Credit Warning Alert */}
        {creditWarning && (
          <div className={cn(
            "flex items-center gap-4 p-4 rounded-xl border",
            creditWarning.color === 'red' && "bg-red-50 border-red-200",
            creditWarning.color === 'amber' && "bg-amber-50 border-amber-200",
            creditWarning.color === 'blue' && "bg-blue-50 border-blue-200"
          )}>
            <div className={cn(
              "w-10 h-10 rounded-lg flex items-center justify-center",
              creditWarning.color === 'red' && "bg-red-100",
              creditWarning.color === 'amber' && "bg-amber-100",
              creditWarning.color === 'blue' && "bg-blue-100"
            )}>
              <AlertTriangle className={cn(
                "w-5 h-5",
                creditWarning.color === 'red' && "text-red-600",
                creditWarning.color === 'amber' && "text-amber-600",
                creditWarning.color === 'blue' && "text-blue-600"
              )} />
            </div>
            <div className="flex-1">
              <p className={cn(
                "font-medium",
                creditWarning.color === 'red' && "text-red-700",
                creditWarning.color === 'amber' && "text-amber-700",
                creditWarning.color === 'blue' && "text-blue-700"
              )}>
                {creditWarning.message}
              </p>
              <p className="text-sm text-[#525252] mt-1">
                You have {credits?.total_available || 0} credits remaining. Purchase more to keep your AI assistant running.
              </p>
            </div>
            <button 
              onClick={() => setShowCreditModal(true)}
              className={cn(
              "px-4 py-2 text-sm font-medium rounded-lg transition-all",
              creditWarning.color === 'red' && "bg-red-600 text-white hover:bg-red-700",
              creditWarning.color === 'amber' && "bg-amber-600 text-white hover:bg-amber-700",
              creditWarning.color === 'blue' && "bg-blue-600 text-white hover:bg-blue-700"
            )}>
              Get Credits
            </button>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-white border border-[#EBEBEB] rounded-xl p-5 hover:shadow-md transition-all" data-testid="credits-stat">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-orange-100 to-pink-100 flex items-center justify-center">
                <Zap className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-orange-500 tracking-tight">{credits?.total_available || 0}</p>
                <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Credits Left</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#EBEBEB] rounded-xl p-5 hover:shadow-md transition-all" data-testid="conversations-stat">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#DBEAFE] flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-[#2563EB]" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-[#0A0A0A] tracking-tight">{analytics?.total_sessions || 0}</p>
                <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Conversations</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#EBEBEB] rounded-xl p-5 hover:shadow-md transition-all" data-testid="messages-stat">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#DCFCE7] flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-[#16A34A]" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-[#16A34A] tracking-tight">{analytics?.total_messages || 0}</p>
                <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Messages</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#EBEBEB] rounded-xl p-5 hover:shadow-md transition-all" data-testid="leads-stat">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#FEF3C7] flex items-center justify-center">
                <UserPlus className="w-5 h-5 text-[#D97706]" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-[#D97706] tracking-tight">{analytics?.total_leads || 0}</p>
                <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Leads Generated</p>
              </div>
            </div>
          </div>
          <div className="bg-white border border-[#EBEBEB] rounded-xl p-5 hover:shadow-md transition-all" data-testid="used-stat">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#F3E8FF] flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-[#9333EA]" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-[#9333EA] tracking-tight">{credits?.credits_used_this_month || analytics?.credits_used_this_month || 0}</p>
                <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Used This Month</p>
              </div>
            </div>
          </div>
        </div>

        {/* AI Feedback Insights */}
        {feedbackData && feedbackData.total > 0 && (
          <div className="bg-white border border-[#EBEBEB] rounded-xl p-6" data-testid="feedback-insights">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="font-semibold text-[#0A0A0A]">AI Satisfaction Insights</h3>
                <p className="text-sm text-[#525252]">How users rate your AI assistant's responses</p>
              </div>
              <div className={cn(
                "text-2xl font-bold px-4 py-1.5 rounded-lg",
                feedbackData.satisfaction_rate >= 80 ? "bg-green-50 text-green-600" :
                feedbackData.satisfaction_rate >= 50 ? "bg-amber-50 text-amber-600" :
                "bg-red-50 text-red-600"
              )}>
                {feedbackData.satisfaction_rate}%
              </div>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-4 mb-5">
              <div className="text-center p-3 bg-[#FAFAFA] rounded-lg">
                <p className="text-xl font-semibold text-[#0A0A0A]">{feedbackData.total}</p>
                <p className="text-xs text-[#A3A3A3]">Total Ratings</p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="flex items-center justify-center gap-1">
                  <ThumbsUp className="w-4 h-4 text-green-500" />
                  <p className="text-xl font-semibold text-green-600">{feedbackData.positive}</p>
                </div>
                <p className="text-xs text-green-600/70">Positive</p>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <div className="flex items-center justify-center gap-1">
                  <ThumbsDown className="w-4 h-4 text-red-400" />
                  <p className="text-xl font-semibold text-red-500">{feedbackData.negative}</p>
                </div>
                <p className="text-xs text-red-400/70">Negative</p>
              </div>
            </div>

            {/* Satisfaction bar */}
            <div className="mb-5">
              <div className="w-full h-2.5 bg-red-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-500 rounded-full transition-all duration-500"
                  style={{ width: `${feedbackData.satisfaction_rate}%` }}
                />
              </div>
            </div>

            {/* Recent negative feedback — actionable insights */}
            {feedbackData.recent_feedback?.filter(f => f.rating === 'down').length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-[#0A0A0A] mb-3">Responses Needing Improvement</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {feedbackData.recent_feedback
                    .filter(f => f.rating === 'down')
                    .slice(0, 5)
                    .map((f, idx) => (
                      <div key={idx} className="p-3 bg-red-50/50 border border-red-100 rounded-lg">
                        {f.user_message && (
                          <p className="text-xs text-[#525252] mb-1">
                            <span className="font-medium">User asked:</span> {f.user_message}
                          </p>
                        )}
                        <p className="text-xs text-red-600">
                          <span className="font-medium">AI said:</span> {f.assistant_message?.slice(0, 150)}
                          {f.assistant_message?.length > 150 ? '...' : ''}
                        </p>
                        <p className="text-[10px] text-[#A3A3A3] mt-1">
                          {f.timestamp ? new Date(f.timestamp).toLocaleDateString() : ''}
                        </p>
                      </div>
                    ))}
                </div>
                <p className="text-xs text-[#A3A3A3] mt-2">
                  Use Q&A Training below to improve responses for these topics.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Basic Settings */}
          <div className="space-y-6">
            {/* Enable/Disable */}
            <div className="bg-white border border-[#EBEBEB] rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-orange-500 to-pink-500 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0A0A0A]">AI Assistant</h3>
                    <p className="text-sm text-[#525252]">Enable AI chatbot for visitors</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config.enabled}
                    onChange={(e) => setConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                </label>
              </div>
            </div>

            {/* Welcome Message */}
            <div className="bg-white border border-[#EBEBEB] rounded-xl p-6">
              <h3 className="font-semibold text-[#0A0A0A] mb-4">Welcome Message</h3>
              <input
                type="text"
                value={config.welcome_message || ''}
                onChange={(e) => setConfig(prev => ({ ...prev, welcome_message: e.target.value }))}
                placeholder="Hi! How can I help you today?"
                className="w-full h-11 px-4 text-sm border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
              />
            </div>

            {/* Personality */}
            <div className="bg-white border border-[#EBEBEB] rounded-xl p-6">
              <h3 className="font-semibold text-[#0A0A0A] mb-4">AI Personality</h3>
              <div className="grid grid-cols-3 gap-3">
                {['friendly', 'professional', 'casual'].map((p) => (
                  <button
                    key={p}
                    onClick={() => setConfig(prev => ({ ...prev, personality: p }))}
                    className={cn(
                      "px-4 py-3 text-sm font-medium rounded-lg border transition-all capitalize",
                      config.personality === p
                        ? "bg-orange-50 border-orange-500 text-orange-600"
                        : "bg-white border-[#E5E5E5] text-[#525252] hover:border-[#D4D4D4]"
                    )}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Instructions */}
            <div className="bg-white border border-[#EBEBEB] rounded-xl p-6">
              <h3 className="font-semibold text-[#0A0A0A] mb-2">Custom Instructions</h3>
              <p className="text-sm text-[#525252] mb-4">Additional context for your AI assistant</p>
              <textarea
                value={config.custom_instructions || ''}
                onChange={(e) => setConfig(prev => ({ ...prev, custom_instructions: e.target.value }))}
                placeholder="E.g., Always mention our 24/7 support, focus on WordPress hosting benefits..."
                rows={4}
                className="w-full px-4 py-3 text-sm border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 resize-none"
              />
            </div>
          </div>

          {/* Right Column - Q&A Training */}
          <div className="bg-white border border-[#EBEBEB] rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="font-semibold text-[#0A0A0A]">Q&A Training</h3>
                <p className="text-sm text-[#525252]">Teach your AI common questions and answers</p>
              </div>
              <span className="text-xs px-2 py-1 bg-[#F5F5F5] text-[#525252] rounded-full">
                {config.qa_pairs?.length || 0} pairs
              </span>
            </div>

            {/* Add New Q&A */}
            <div className="mb-6 p-4 bg-[#FAFAFA] rounded-xl">
              <div className="space-y-3">
                <input
                  type="text"
                  value={newQA.question}
                  onChange={(e) => setNewQA(prev => ({ ...prev, question: e.target.value }))}
                  placeholder="Question: e.g., What is your refund policy?"
                  className="w-full h-10 px-4 text-sm border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                />
                <textarea
                  value={newQA.answer}
                  onChange={(e) => setNewQA(prev => ({ ...prev, answer: e.target.value }))}
                  placeholder="Answer: e.g., We offer a 30-day money-back guarantee..."
                  rows={3}
                  className="w-full px-4 py-3 text-sm border border-[#E5E5E5] rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 resize-none"
                />
                <button
                  onClick={addQAPair}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-orange-500 to-pink-500 rounded-lg hover:shadow-md transition-all"
                >
                  <Plus className="w-4 h-4" />
                  Add Q&A Pair
                </button>
              </div>
            </div>

            {/* Existing Q&A Pairs */}
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {config.qa_pairs?.length === 0 ? (
                <div className="text-center py-8 text-[#A3A3A3]">
                  <MessageCircle className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No Q&A pairs yet</p>
                  <p className="text-xs">Add your first one above</p>
                </div>
              ) : (
                config.qa_pairs?.map((qa, idx) => (
                  <div key={idx} className="p-4 border border-[#EBEBEB] rounded-xl hover:border-[#D4D4D4] transition-all">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#0A0A0A] mb-1">Q: {qa.question}</p>
                        <p className="text-sm text-[#525252]">A: {qa.answer}</p>
                      </div>
                      <button
                        onClick={() => deleteQAPair(idx)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-all flex-shrink-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Credit Purchase Modal */}
        {showCreditModal && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowCreditModal(false)}>
            <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-[#0A0A0A]">Purchase AI Credits</h2>
                  <p className="text-sm text-[#525252] mt-1">Power your AI assistant with more credits</p>
                </div>
                <button 
                  onClick={() => setShowCreditModal(false)}
                  className="p-2 hover:bg-[#F5F5F5] rounded-lg transition-all"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {creditPackages.map((pkg) => (
                  <div 
                    key={pkg.id}
                    className={cn(
                      "relative p-5 rounded-xl border-2 transition-all cursor-pointer hover:shadow-md",
                      pkg.popular ? "border-orange-500 bg-orange-50/50" : "border-[#EBEBEB] hover:border-[#D4D4D4]"
                    )}
                    data-testid={`credit-package-${pkg.id}`}
                  >
                    {pkg.popular && (
                      <span className="absolute -top-3 left-4 px-2 py-0.5 bg-orange-500 text-white text-xs font-medium rounded-full">
                        Most Popular
                      </span>
                    )}
                    <div className="mb-4">
                      <h3 className="font-semibold text-[#0A0A0A]">{pkg.name}</h3>
                      <p className="text-sm text-[#525252] mt-1">{pkg.credits} AI messages</p>
                    </div>
                    <div className="flex items-baseline gap-1 mb-4">
                      <span className="text-3xl font-bold text-[#0A0A0A]">${pkg.price}</span>
                      <span className="text-sm text-[#525252]">one-time</span>
                    </div>
                    <button
                      onClick={() => purchaseCredits(pkg.id)}
                      disabled={purchasingPackage === pkg.id}
                      className={cn(
                        "w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all",
                        pkg.popular 
                          ? "bg-orange-500 text-white hover:bg-orange-600" 
                          : "bg-black text-white hover:bg-[#262626]",
                        purchasingPackage === pkg.id && "opacity-50 cursor-not-allowed"
                      )}
                    >
                      {purchasingPackage === pkg.id ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4" />
                          Purchase
                        </>
                      )}
                    </button>
                  </div>
                ))}
              </div>

              <p className="text-xs text-center text-[#A3A3A3] mt-6">
                Secure payment powered by Stripe. Credits never expire.
              </p>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
